<script setup lang="ts">
/**
 * Шаблон «Компактная» — текст слева, цена и кнопка в подвале.
 * Вертикально экономнее классической: в тот же экран влезает больше позиций.
 */
import { PRODUCT_CARD_PROP_DEFAULTS, type ProductCardEmits, type ProductCardProps } from './cardContract';
import { getCardDescription } from '@entities/product';

const props = withDefaults(defineProps<ProductCardProps>(), PRODUCT_CARD_PROP_DEFAULTS);
const emit = defineEmits<ProductCardEmits>();

const description = computed(() => getCardDescription(props.item));
/**
 * Кнопка с подписью в узкой колонке не встаёт рядом с ценой — уводим её
 * под цену на всю ширину. Кнопка-иконка помещается в строку.
 */
const stacked = computed(() => props.config.actionStyle !== 'icon');
</script>

<template>
  <div
    class="pcard pcard--compact"
    :class="[`pcard--surface-${config.surface}`]"
    :data-testid="`product-card-${item.id}`"
    @click="emit('open')"
  >
    <ProductBadges
      v-if="config.showBadges"
      :tags="item.tags"
      :teg="item.teg"
      size="xs"
      :max-visible="3"
      class-name="pcard__badges"
    />

    <ProductCardFavorite
      v-if="favoriteEnabled"
      class="pcard__favorite"
      :active="!!item.is_favorite"
      :animation="favoriteAnimation"
      @click="emit('favorite')"
    />

    <ProductCardMedia :item="item" :aspect="config.imageAspect" :placeholder="placeholder" :priority="priority" />

    <div class="pcard__body">
      <h3 class="pcard__title">
        <!-- Ссылка, а не текст: карточка открывается модалкой, и без href у блюда
             не было ни одного адреса, по которому его мог бы найти поиск. Клик
             гасим — поведение витрины остаётся прежним, но Ctrl+клик и
             «скопировать адрес ссылки» теперь работают. -->
        <a v-if="href" :href="href" class="pcard__title-link" @click.prevent="emit('open')">{{ item.name }}</a>
        <template v-else>{{ item.name }}</template>
      </h3>
      <p v-if="config.showDescription && description" class="pcard__description">{{ description }}</p>
    </div>

    <div class="pcard__bottom" :class="{ 'pcard__bottom--stacked': stacked }">
      <ProductCardPrice class="pcard__price" :item="item" :variant="config.priceStyle" />
      <ProductCardAction
        :block="stacked"
        :in-cart="inCart"
        :cart-quantity="cartQuantity"
        :variant="config.actionStyle"
        :data-testid="inCart ? `product-card-incart-${item.id}` : `product-card-add-${item.id}`"
        @click="emit('action')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.pcard--compact .pcard__bottom--stacked {
  @apply flex-col items-stretch gap-2;
}
</style>
